import * as React from "react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

export type ModalSize = "sm" | "md" | "lg";

// Escala de largura única do sistema de modais: sm (confirm/login),
// md (forms), lg (Produto). Acaba com os max-w-md/[480px]/[550px] avulsos.
const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "sm:max-w-[380px]",
  md: "sm:max-w-[480px]",
  lg: "sm:max-w-[560px]",
};

export interface ModalShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  size?: ModalSize;
  footer?: React.ReactNode;
  children: React.ReactNode;
  // Fluxos travados (ex: login obrigatório) passam false pra não fechar no
  // clique fora / Esc.
  dismissable?: boolean;
  contentClassName?: string;
  bodyClassName?: string;
}

export function ModalShell({
  open,
  onOpenChange,
  title,
  subtitle,
  size = "md",
  footer,
  children,
  dismissable = true,
  contentClassName,
  bodyClassName,
}: ModalShellProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // p-0/gap-0 sobrescrevem o p-6/gap-4 do DialogContent base; o
        // max-w-[calc(100vw-2rem)] derruba o max-w-lg base no mobile pra nunca
        // estourar o viewport (a escala sm/md/lg assume no >=640px).
        className={cn(
          "flex max-h-[90vh] w-full max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0",
          SIZE_CLASS[size],
          contentClassName,
        )}
        onInteractOutside={dismissable ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={dismissable ? undefined : (e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1 border-b border-border px-5 py-4 pr-12">
          {/* min-h-8 + items-center: o X do DialogContent é uma caixa de 32px
              na mesma altura (top-4); sem isso o título fica ~7px acima. */}
          <DialogTitle className="flex min-h-8 items-center font-display text-lg font-bold leading-none tracking-tight">
            {title}
          </DialogTitle>
          {subtitle ? (
            <DialogDescription className="text-xs leading-snug text-muted-foreground">
              {subtitle}
            </DialogDescription>
          ) : null}
        </div>

        <div
          className={cn(
            "flex flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-4",
            bodyClassName,
          )}
        >
          {children}
        </div>

        {footer ? (
          <div className="flex flex-col-reverse gap-2.5 border-t border-border bg-muted/30 px-5 py-3.5 sm:flex-row sm:justify-end">
            {footer}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
